import React, { useMemo, useState } from 'react';
import { Button, Input, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderOptionDto } from '../dto/order';
import { getTransportOrderProductDisplayName } from './sharedTransportFormRows';
import './TransportOrderPickerField.css';

const { Text } = Typography;

/**
 * Search-first customer-order picker for the transport form.
 *
 * Operator feedback (2026-09): the previous always-visible, paginated table of
 * every open order was a "wall" that had to be scrolled before anything else
 * on the page. The picker (rendered inside the "Dodaj zamówienia" modal) shows
 * a search box; candidate rows appear only while the operator is typing.
 * `TransportSelectedOrdersList` renders what is already on the truck, inline
 * in the form section. Selection state stays in SharedTransportForm
 * (`orderRowSelection`); these components own filtering and presentation only.
 */
export interface TransportOrderRowSelection {
  selectedRowKeys: string[];
  onChange: (keys: React.Key[]) => void;
}

interface TransportOrderPickerFieldProps {
  availableOrders: OrderOptionDto[];
  availableColumns: ColumnsType<OrderOptionDto>;
  orderRowSelection: TransportOrderRowSelection;
}

export function filterTransportOrderCandidates(
  orders: OrderOptionDto[],
  query: string,
): OrderOptionDto[] {
  const needle = query.trim().toLocaleLowerCase('pl');
  if (!needle) return [];
  return orders.filter((order) => {
    const searchText = [
      order.displayOrderNumber,
      order.orderNumber,
      order.clientOrderNumber,
      order.customerName,
      order.customer?.name,
      ...(order.products || []).map(getTransportOrderProductDisplayName),
    ].filter(Boolean).join(' ').toLocaleLowerCase('pl');
    return searchText.includes(needle);
  });
}

export function selectTransportOrders(
  availableOrders: OrderOptionDto[],
  selectedRowKeys: string[],
): OrderOptionDto[] {
  const selectedIds = new Set(selectedRowKeys);
  return availableOrders.filter((order) => selectedIds.has(order.id));
}

const TransportOrderPickerField: React.FC<TransportOrderPickerFieldProps> = ({
  availableOrders,
  availableColumns,
  orderRowSelection,
}) => {
  const { t } = useTranslation(['transport', 'orders', 'common']);
  const [search, setSearch] = useState('');
  const query = search.trim();

  const selectedCount = useMemo(
    () => selectTransportOrders(availableOrders, orderRowSelection.selectedRowKeys).length,
    [availableOrders, orderRowSelection.selectedRowKeys],
  );

  const candidates = useMemo(
    () => filterTransportOrderCandidates(availableOrders, query),
    [availableOrders, query],
  );

  return (
    <div className="transport-order-picker" data-testid="transport-order-picker">
      <div className="transport-order-picker__toolbar">
        <Input
          allowClear
          autoFocus
          className="transport-order-picker__search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          prefix={<SearchOutlined aria-hidden="true" />}
          placeholder={t('transport:orderPicker.searchPlaceholder')}
          aria-label={t('transport:orderPicker.searchPlaceholder')}
          data-testid="transport-order-picker-search"
        />
        <div className="transport-order-picker__selection-summary" aria-live="polite">
          <Text type={selectedCount > 0 ? undefined : 'secondary'}>
            {t('transport:orderPicker.selectedCount', { count: selectedCount })}
          </Text>
          {selectedCount > 0 ? (
            <Button type="link" size="small" onClick={() => orderRowSelection.onChange([])}>
              {t('common:clear')}
            </Button>
          ) : null}
        </div>
      </div>

      {query ? (
        <Table<OrderOptionDto>
          className="transport-order-picker-table"
          data-testid="transport-order-picker-table"
          rowKey="id"
          size="small"
          pagination={{ pageSize: 8, hideOnSinglePage: true }}
          rowSelection={{ ...orderRowSelection, preserveSelectedRowKeys: true }}
          dataSource={candidates}
          columns={availableColumns}
          locale={{ emptyText: t('transport:orderPicker.noResults', { query }) }}
          onRow={(order) => ({
            onClick: (event) => {
              const target = event.target as HTMLElement;
              if (target.closest('button, a, input, label, .ant-checkbox-wrapper')) return;
              const keys = orderRowSelection.selectedRowKeys;
              orderRowSelection.onChange(
                keys.includes(order.id) ? keys.filter((key) => key !== order.id) : [...keys, order.id],
              );
            },
          })}
        />
      ) : (
        <Text type="secondary" className="transport-order-picker__hint">
          {t('transport:orderPicker.searchHint')}
        </Text>
      )}
    </div>
  );
};

interface TransportSelectedOrdersListProps {
  availableOrders: OrderOptionDto[];
  orderRowSelection: TransportOrderRowSelection;
}

export const TransportSelectedOrdersList: React.FC<TransportSelectedOrdersListProps> = ({
  availableOrders,
  orderRowSelection,
}) => {
  const { t } = useTranslation(['transport', 'orders', 'common']);
  const selectedOrders = useMemo(
    () => selectTransportOrders(availableOrders, orderRowSelection.selectedRowKeys),
    [availableOrders, orderRowSelection.selectedRowKeys],
  );

  if (selectedOrders.length === 0) return null;

  return (
    <ul
      className="transport-order-picker__selected"
      data-testid="transport-order-picker-selected"
      aria-label={t('transport:selectedOrders')}
    >
      {selectedOrders.map((order) => {
        const number = order.displayOrderNumber || order.orderNumber;
        const products = (order.products || []).map(getTransportOrderProductDisplayName).join(', ');
        return (
          <li
            key={order.id}
            className="transport-order-picker__selected-item"
            data-testid={`transport-order-picker-selected-${order.id}`}
          >
            <div className="transport-order-picker__selected-main">
              <div className="transport-order-picker__selected-head">
                <Text strong className="transport-order-picker__selected-number">{number}</Text>
                <Text className="transport-order-picker__selected-customer">
                  {order.customer?.name || order.customerName || '—'}
                </Text>
                <Tag className="transport-order-picker__selected-status">
                  {t(`orders:statusOptions.${order.status}`, {
                    defaultValue: t('common:unknownStatus'),
                  })}
                </Tag>
              </div>
              {products ? (
                <Text type="secondary" className="transport-order-picker__selected-products">
                  {products}
                </Text>
              ) : null}
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              aria-label={t('transport:orderPicker.remove', { number })}
              onClick={() => orderRowSelection.onChange(
                orderRowSelection.selectedRowKeys.filter((key) => key !== order.id),
              )}
            />
          </li>
        );
      })}
    </ul>
  );
};

export default TransportOrderPickerField;
